import { Alert, Button, Card, CardBody, CardHeader } from '@heroui/react';
import { Icon } from '@iconify/react';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDismissable } from '@/context/DismissableContext';
import { Patient } from '@/patient-app-common/types/Patient';
import { useAppSelector } from '@/redux/hooks';

import { DateModalSurgery } from '../../prompts/DateModal';

interface PreopCardProps {
  patient: Patient;
  surgeryDate: Dayjs | null;
  startOfDayInHospitalTz: Dayjs | null;
}

const DISMISS_ID = 'phase_card';

export function PreopCard({
  patient,
  surgeryDate,
  startOfDayInHospitalTz,
}: PreopCardProps) {
  const { t } = useTranslation('home');
  const { appVariant } = useAppSelector((state) => state.app);
  const [dateModal, toggleDateModal] = useState<boolean>(false);
  const { sessionDismissed, dismissAlert } = useDismissable();

  const {
    hasPeriop,
    hasPostop,
    patientsCanChangeSurgeryDate,
    procedureName,
    firstName,
    othersUsingApp,
  } = patient;
  const headerText = othersUsingApp
    ? t('common.text.greeting_name_indirect', { firstName })
    : t('common.text.greeting_name', { firstName });

  const title_date = othersUsingApp
    ? t('preopcard.title_date_indirect', {
        firstName,
        procedureName,
        date: dayjs(surgeryDate).format('LL'),
      })
    : t('preopcard.title_date', {
        procedureName,
        date: dayjs(surgeryDate).format('LL'),
      });

  const title_nodate = othersUsingApp
    ? t('preopcard.title_nodate_indirect', {
        firstName,
        procedureName,
      })
    : t('preopcard.title_nodate', { procedureName });

  const preopOnly = !hasPeriop && !hasPostop;
  const canChangeSurgeryDate = patientsCanChangeSurgeryDate && !preopOnly;

  const hasSurgeryDate = !_.isEmpty(surgeryDate);

  const daysToSurgery = dayjs(surgeryDate).diff(startOfDayInHospitalTz, 'days');

  const title = hasSurgeryDate ? title_date : title_nodate;

  return (
    <>
      <DateModalSurgery
        isOpen={dateModal}
        onClose={() => toggleDateModal(false)}
        title={t('preopcard.set_surgerydate', { context: appVariant })}
      />
      <Card shadow="none" className="bg-transparent px-0">
        <CardHeader className="bg-transparent px-0">
          <h1 className="text-2xl">{headerText}</h1>
        </CardHeader>
        <CardBody className="px-0">
          <h2 className="font-light">{title}</h2>
          <p className="font-light">
            {hasSurgeryDate && (
              <span>{t('preopcard.countdown', { count: daysToSurgery })}</span>
            )}{' '}
            {t('preopcard.description')}
          </p>
        </CardBody>
      </Card>
      {canChangeSurgeryDate && (
        <Alert
          color="primary"
          description={t('preopcard.set_surgerydate', { context: appVariant })}
          icon={<Icon icon="fa-solid:calendar-day" width="20" />}
          isVisible={!sessionDismissed[DISMISS_ID]}
          hideIconWrapper
          variant="faded"
        >
          <div className="inline-flex mt-4">
            <Button
              color="primary"
              onPress={() => toggleDateModal(true)}
              size="sm"
            >
              {t('common.action.update')}
            </Button>
            <Button
              className="ml-4"
              variant="light"
              size="sm"
              onPress={() => dismissAlert(DISMISS_ID)}
            >
              {t('common.action.dismiss')}
            </Button>
          </div>
        </Alert>
      )}
    </>
  );
}
